// @flow

import type {Agency} from 'src/api-parsers/index.types';
import type {Dispatch} from 'src/reducers';
import type {RouteLocation} from 'src/types/router';
import IndexStore from 'src/stores/index';
import {setAIPanelOpenEvent} from 'src/action-creators/sense-ai-panel.js';

import {useEnvironmentContext} from 'src/hooks/useEnvironmentContext';
import * as React from 'react';

import * as reduxApi from 'src/utils/redux-api-v2';
import {usePageTitle} from 'src/contexts/page-title-context.jsx';
import {connect, useSelector} from 'react-redux';
import flow from 'lodash/flow';
import {provideHooks} from 'src/flux/provideHooks.jsx';
import {withWindowWidth} from 'src/flux/responsive.jsx';
import {fluxify} from 'src/flux/component.jsx';
import {setAuthPath} from 'src/actions';
import useI18n from 'src/hooks/useI18n';
import {getAgency} from 'src/action-creators/agency';
import {getAllEntities} from 'src/action-creators/ats-entities';
import {classify} from 'src/utils';
import {isChatAppLocation, isMobile} from 'src/utils/responsive';
import {getFromAuthedUser, hasPrivilege} from 'src/utils/accounts';
import {selectDisplayPhoneNumber, agentHasPhone} from 'src/selectors/chat';
import {selectCurrentPhoneCountry} from 'src/selectors/phone-number-sets';
import {selectFullscreen} from 'src/selectors/app-config.js';

import WalkmeButton from 'src/components/navigation/walkme-button.jsx';
import {RouterErrorBoundary} from 'src/components/500/boundary.jsx';
import Loader from 'src/components/navigation/loader.jsx';
import Notification from 'src/components/navigation/notification.jsx';
import BadBrowserNotification from 'src/components/navigation/bad-browser-notification.jsx';
import MessagesSocket from 'src/components/messaging/socket.jsx';
import {PageTitle} from 'src/components/app-shell/page-title.jsx';

import {selectTotalUnreadThreadCount} from 'src/selectors/messages';
import {useWindowSize} from '@spaced-out/ui-design-system/lib/hooks/useWindowSize';

import AppShellSideNavContainer from 'src/components/app-shell/app-shell-side-nav-container.jsx';

import css from 'src/components/navigation/top.css';

import appShellCss from './app-shell-container.css';

import {
  USE_MESSAGING,
  USE_JD_GENERATOR,
  USE_PRESCREENING_GENERATOR,
  USE_RESUME_GENERATOR,
} from 'src/action-creators/roles';

import {
  getAppShellStatus,
  setAppShellStatus,
} from 'src/utils/app-shell-storage';


type OuterProps = {
  children: React.Node,
};

type TopNavigationProps = {
  ...OuterProps,
  // $FlowFixMe[value-as-type]
  store: IndexStore,
  authed: boolean,
  agency: Agency,
  name: ?string,
  dispatch: Dispatch,
  location: RouteLocation,
  params: Object,
  groupName: ?string,
  authedUserPhone: ?string,
  isMobileWindow: boolean,
  isMobile: boolean,
  context: string,
  currentPhoneCountry: string,
  audienceFilterSeparation?: boolean,
  totalUnreadCount: number,
  canViewChat: boolean,
  setIsAiPanelOpen: (value: boolean) => void,
  showAIPanel: boolean,
  hasSenseAIPermission: boolean,
  handleSenseUniversityOnClick: () => void,
};

const dependencies = {
  required: ({dispatch}) => dispatch(getAgency()),
  // NOTE (kyle): ats entities are needed almost everywhere in our application.
  deferred: ({dispatch}) => dispatch(getAllEntities()),
};

const mapStateToProps = (state) => {
  return {
    agency: state.agency.agency,
    authed: state.accounts.authedUserId !== null,
    name: getFromAuthedUser(state, 'name'),
    groupName: getFromAuthedUser(state, ['securityGroup', 'name']),
    authedUserPhone: selectDisplayPhoneNumber(state),
    isMobile: isMobile(state.requestHeaders.headers['user-agent']),
    context: state.env.query.context,
    currentPhoneCountry: selectCurrentPhoneCountry(state),
    totalUnreadCount: selectTotalUnreadThreadCount(state),
    canViewChat: agentHasPhone(state) && hasPrivilege(state, USE_MESSAGING),
    showAIPanel: state.senseAIPanel.isAiPanelOpen,
    hasSenseAIPermission:
      hasPrivilege(state, USE_JD_GENERATOR) ||
      hasPrivilege(state, USE_PRESCREENING_GENERATOR) ||
      hasPrivilege(state, USE_RESUME_GENERATOR),
  };
};

const mapDispatchToProps = (dispatch) => ({
  setIsAiPanelOpen(value) {
    dispatch(setAIPanelOpenEvent(value));
  },
  async handleSenseUniversityOnClick() {
    await dispatch(reduxApi.get('sense-university/login')).then((url) => {
      window.open(url, '_blank');
    });
  },
});

function AppShellContainer(props: TopNavigationProps): React.Node {
  const {
    store,
    location,
    authed,
    children,
    name,
    agency: {activeEventProcessor, slug},
    isMobile,
    isMobileWindow,
    context,
    setIsAiPanelOpen,
    handleSenseUniversityOnClick,
  } = props;

  const {pathname} = location;
  const {labelI18nInstance} = useI18n();
  const t = labelI18nInstance.t;

  const defaultState = true;

  const [stateExpand, setExpand] = React.useState(
    getAppShellStatus() ? false : defaultState,
  );

  const {
    pageTitleState: {tabSlotComponent, hidePageTitle},
  } = usePageTitle();

  React.useEffect(() => {
    setAppShellStatus(stateExpand.toString());
  }, [stateExpand]);

  const isFullscreenExperience = useSelector(selectFullscreen);

  const [showMenu, setShowMenu] = React.useState(false);
  const {width} = useWindowSize();
  const {isExtension} = useEnvironmentContext();
  const extensionProductList = [
    '/messages',
    '/contacts',
    '/referralv2',
    '/sense-ai-panel',
    '/meetings',
    '/jobs/all',
  ];
  const isAppSupportedInExtension =
    width > 600 &&
    extensionProductList.some((extPath) => pathname.startsWith(extPath));

  React.useEffect(() => {
    if (showMenu) {
      const handleBodyClick = () => {
        setShowMenu(false);
      };

      document.addEventListener('click', handleBodyClick);

      return () => {
        document.removeEventListener('click', handleBodyClick);
      };
    }
  }, [showMenu]);

  React.useEffect(() => {
    setAuthPath(store, '/signin');
    const handlePostMessage = (event) => {
      if (event?.data?.type === 'CLOSE_AI_PANEL') {
        setIsAiPanelOpen(event.data.showAIPanel);
      }
    };
    window.addEventListener('message', handlePostMessage);
    return () => {
      setAuthPath(store, null);
      window.removeEventListener('message', handlePostMessage);
    };
  }, []);

  const topNavHidden =
    context === 'pwa' ||
    context === 'trm-iframe' ||
    context === 'sense-ai' ||
    isMobile ||
    (isChatAppLocation(location) && isMobileWindow) ||
    isFullscreenExperience;

  const fullyHidden = isExtension || topNavHidden;

  return (
    <div className={css.container}>
      <div
        className={classify(appShellCss.appShellContainer, {
          [appShellCss.withoutHeader]: topNavHidden,
        })}
      >
        <div className={appShellCss.sideNavigationBar}>
          <AppShellSideNavContainer
            expanded={stateExpand}
            setExpand={setExpand}
            name={name}
            handleSenseUniversityOnClick={handleSenseUniversityOnClick}
          />
        </div>
        <>
          {!topNavHidden && !hidePageTitle && (
            <div className={appShellCss.topNavigationBar}>
              <PageTitle />
            </div>
          )}
          <div
            className={classify(appShellCss.contentArea, {
              [appShellCss.contentAreaWithoutHeader]:
                topNavHidden || hidePageTitle,
              [appShellCss.contentAreaWithTabSlot]: !!tabSlotComponent,
            })}
          >
            {authed &&
              !activeEventProcessor &&
              slug !== 'demo' &&
              process.env.NODE_ENV ===
                'production' /* aka "is production or staging" */ && (
                /* $FlowIssue - children prop as JSX not recognized by FlowType */
                <Notification className={appShellCss.notification}>
                  <strong>
                    {t(
                      'Your_account_is_in_setup_mode',
                      'Your account is in setup mode,',
                    )}
                  </strong>
                  <span>
                    {`${t(
                      'so_no_automated_messages_will_be_sent',
                      'so no automated messages will be sent.',
                    )} ${t(
                      'To_enable_your_account_and_go_live_please_contact_Sense',
                      'To enable your account and go live, please contact Sense.',
                    )}`}
                  </span>
                </Notification>
              )}
            <BadBrowserNotification />
            <div
              className={classify(appShellCss.child, {
                [appShellCss.extensionResponsiveness]:
                  isExtension || isAppSupportedInExtension,
              })}
            >
              <RouterErrorBoundary>{children}</RouterErrorBoundary>
            </div>
          </div>
        </>
      </div>
      <Loader />

      {/*
          Open WebSocket even if TopNavBar is fully hidden by the chrome-extension.
          TODO (gab): Eventually move socket into main app component (must first decouple
          surveys from main app)
        */}
      <MessagesSocket />

      {!fullyHidden && (
        <div className={css.walkmeContainer}>
          <WalkmeButton className={css.walkme} />
        </div>
      )}
    </div>
  );
}

export default (flow(
  withWindowWidth,
  // $FlowFixMe[prop-missing] this component works so editing it would just be a risk
  fluxify({}, {pure: true}),
  connect(mapStateToProps, mapDispatchToProps),
  provideHooks(dependencies),
)(AppShellContainer): React.ComponentType<OuterProps>);
